<template>
  <div>
    <form>
      <div class="text-center w-full">
        <p class="text-24 md:text-47 font-bold">Create account</p>
        <p class="text-20 text-grey-20">Welcome to hello ivy. Let’s get you started, for free.</p>
      </div>

      <InputModule
        ref="emailInputRef"
        v-model="email"
        name="email"
        type="email"
        label="Email address"
        placeholder="alex@email.com"
        class="w-full mt-30"
        :readonly="hasInvite"
        input-classes="px-22 py-13 rounded-10 border border-grey-10 focus:border-brand anim-all text-17"
        label-class="mb-5 font-bold"
        shadow="focus:shadow-signup"
        :has-background="false"
        :error-message="errorMessages.email"
        :is-need-link="isNeedLink"
        @clearErrorMessage="clearErrorMessage"
      />

      <PasswordInput
        v-model="password"
        name="password"
        type="password"
        label="Password"
        placeholder="••••••••••"
        class="w-full mt-20"
        input-classes="px-22 py-13 md:py-7 rounded-10 border border-grey-10 focus:border-brand anim-all md:text-24"
        label-class="mb-5 font-bold"
        :has-background="false"
        :error-message="errorMessages.password"
        show-password-power
        @clearErrorMessage="clearErrorMessage"
      />

      <ButtonModule
        text="Continue"
        suffix="relative icon-arrow-right ml-10 mt-2 text-10 left-0 group-hover:left-7 anim-all"
        class="w-full justify-center bg-brand hover:bg-brand-hover text-white rounded-10 mt-30 py-16 group font-medium text-17"
        @click.prevent="continueRegister"
      />

      <p class="text-grey-275 text-15 text-center mt-15">No credit card required</p>

      <SocialAuth v-if="false" text="Sign up" class="mt-10 md:mt-30" />

      <div class="mt-50 text-center w-full text-15">
        <p class="text-grey-275">Already have an account?</p>
        <router-link :to="url" class="text-brand mt-5 hover:underline mb-10 md:mb-35">Log in here</router-link>
      </div>
    </form>
  </div>
</template>

<script>
import { defineComponent, ref, reactive, computed, onMounted, nextTick } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'

import { requiredFieldsValidation, emailValidation, passwordValidation } from '@/helpers'
import { teamsService } from '@/services'

import ButtonModule from '@/components/shared/form-controls/ButtonModule'
import InputModule from '@/components/shared/form-controls/InputModule'
import PasswordInput from '@/components/shared/form-controls/PasswordInput'
import SocialAuth from '@/components/auth/SocialAuth'

export default defineComponent({
  name: 'SignUpTypes',

  components: {
    ButtonModule,
    InputModule,
    PasswordInput,
    SocialAuth
  },

  props: {
    hasInvite: Boolean
  },

  emits: ['next', 'update:modelValue', 'setExistingAccount', 'nextToStep3'],

  setup (props, { emit }) {
    const { commit, getters } = useStore()
    const route = useRoute()

    const emailInputRef = ref(null)
    const loginModalRef = ref(null)

    const deletedAt = ref(null)
    const signUpType = ref('personal')
    const isNeedLink = ref(false)
    const hasAccount = ref(false)

    const password = ref('')
    const email = ref(route.query.email || null)

    const errorMessages = reactive({
      email: null,
      password: null
    })

    const url = computed(() => route.query ? { name: 'Login', query: route.query } : { name: 'Login' })
    const isDarkMode = computed(() => getters.isDarkMode)

    onMounted(() => {
      nextTick(() => {
        emailInputRef.value.focus()
      })
    })

    function clearErrorMessage (name) {
      errorMessages[name] = null
    }

    function continueRegister () {
      requiredFieldsValidation(errorMessages, {
        email: email.value,
        password: password.value
      })

      if (!errorMessages.email) errorMessages.email = emailValidation(email.value)
      if (!errorMessages.password) errorMessages.password = passwordValidation(password.value)

      if (!errorMessages.email && !errorMessages.password) {
        teamsService.checkEmail(email.value).then(({ data }) => {
          emit('update:modelValue', {
            email: email.value,
            password: password.value
          })

          if (data.hasTeamAccount && (data.hasPersonalAccount || props.hasInvite)) {
            errorMessages.email = 'A user user already exists. '
            isNeedLink.value = true
          } else if (data.hasTeamAccount || data.hasPersonalAccount) {
            const type = data.hasTeamAccount ? 'teams' : 'personal'
            commit('setNotification', { isVisible: true, message: `A user with this e-mail address already has ${type} account.` })
            emit('setExistingAccount', type)
            hasAccount.value = type
          } else {
            if (props.hasInvite) return emit('nextToStep3')
            emit('next')
          }
        })
      }
    }

    return {
      // refs
      loginModalRef,
      emailInputRef,
      // data
      email,
      password,
      deletedAt,
      isNeedLink,
      signUpType,
      errorMessages,
      // computed
      url,
      isDarkMode,
      // function
      continueRegister,
      clearErrorMessage
    }
  }
})
</script>
